import React, { useMemo } from 'react'
import DashboardStackedBarChart from '@components/pages/Dashboard/MetricCharts/DashboardStackedBarChart.component'
import {
  FINANCE_PRODUCTION_BY_PROCEDURE_BAR_CATEGORY_GAP,
  FINANCE_PRODUCTION_BY_PROCEDURE_BAR_SIZE,
} from '@constants/Dashboard/financeDashboard.constants'
import { formatProductionThousands } from '@utils/dashboardFinance.util'
import {
  computeProductionByProcedureCategoryAxisWidth,
  computeProductionByProcedureChartHeight,
} from '@utils/productionByProcedureCategory.util'
import { FinanceSeriesLegend } from '../../FinanceMetricTiles.component'

type ChartRow = {
  label: string
  value: number
  total: number
}

type ChartAxisConfig = {
  yDomain: [number, number]
  yTicks: number[]
}

type ProductionByProcedureChartProps = {
  data: ChartRow[]
  axis: ChartAxisConfig
  categoryColors: Record<string, string>
  className?: string
  /** Dashboard card: vertical bars + legend. Detail page: horizontal bars. */
  orientation?: 'vertical' | 'horizontal'
}

const CARD_CHART_MIN_HEIGHT = 140

/** Vertical bars on Finance / Performance dashboard cards (4 categories). */
const CARD_VERTICAL_BAR_PROPS = {
  barSize: FINANCE_PRODUCTION_BY_PROCEDURE_BAR_SIZE,
  barCategoryGap: FINANCE_PRODUCTION_BY_PROCEDURE_BAR_CATEGORY_GAP,
} as const

export function ProductionByProcedureChart({
  data,
  axis,
  categoryColors,
  className = 'dashboard-finance-production-by-procedure-chart',
  orientation = 'horizontal',
}: ProductionByProcedureChartProps): React.ReactElement {
  const categoryAxisWidth = useMemo(
    () =>
      computeProductionByProcedureCategoryAxisWidth(
        data.map((row) => row.label),
      ),
    [data],
  )
  const chartHeight = useMemo(() => {
    if (orientation === 'vertical') return CARD_CHART_MIN_HEIGHT
    return computeProductionByProcedureChartHeight(data.length)
  }, [data.length, orientation])

  const verticalChart = (
    <DashboardStackedBarChart
      data={data}
      series={[{ dataKey: 'value', name: '', color: '' }]}
      horizontal={false}
      stacked={false}
      categoryIndicatorColors={categoryColors}
      hideCategoryIndicatorCircles
      hideCategoryAxisLabels
      valueFormatter={formatProductionThousands}
      height={chartHeight}
      fillHeight
      showLegend={false}
      yAxisDomain={axis.yDomain}
      yAxisTicks={axis.yTicks}
      margin={{ left: 0, right: 12, bottom: 4 }}
      className={className}
      {...CARD_VERTICAL_BAR_PROPS}
    />
  )

  const chart =
    orientation === 'vertical' ? (
      verticalChart
    ) : (
      <DashboardStackedBarChart
        data={data}
        series={[{ dataKey: 'value', name: '', color: '' }]}
        horizontal
        stacked={false}
        categoryIndicatorColors={categoryColors}
        hideCategoryIndicatorCircles
        valueFormatter={formatProductionThousands}
        height={chartHeight}
        showLegend={false}
        xAxisDomain={axis.yDomain}
        xAxisTicks={axis.yTicks}
        yAxisWidth={categoryAxisWidth}
        margin={{ left: 4, right: 16, top: 8, bottom: 8 }}
        className={className}
      />
    )

  if (orientation === 'vertical') {
    return (
      <div className="dashboard-finance-treatment-value-chart-wrap dashboard-finance-bar-chart-legend-spaced dashboard-finance-production-by-procedure-chart-wrap">
        <div className="dashboard-finance-production-by-procedure-chart-area">
          {verticalChart}
        </div>
        <FinanceSeriesLegend
          className="dashboard-finance-production-legend dashboard-finance-production-by-procedure-legend"
          series={data.map((row) => ({
            name: row.label,
            color: categoryColors[row.label] ?? 'var(--blue-400)',
          }))}
        />
      </div>
    )
  }

  return chart
}
